var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useMemo } from 'react';
import { UXPContext } from '@core/context/Context';
import Modal from '@widget/popups/Modal';
import DynamicForm from '@widget/forms/DynamicForm/DynamicForm';
import { convertToNewFormStructure, lookupComponent } from '@utils';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import { getMergedWidgetProps, updateConfigPropsWithValues } from '../dashboardUtils';
import classNames from 'classnames';
const DashboardWidgetConfig = ({ widget, onClose, onSave }) => {
    const context = useContext(UXPContext);
    const { formStructure, currentProps, ConfigPanel } = useMemo(() => {
        var _a, _b, _c, _d, _e;
        if (!widget) {
            return {
                formStructure: [],
                currentProps: {},
                ConfigPanel: null
            };
        }
        // Get merged props (defaultProps merged with saved props)
        const props = getMergedWidgetProps(widget);
        // Always use the REGISTERED widget's config props so that live function
        // references (show, getOptions, validate) are never lost after JSON round-trip.
        // Fall back to widget.configs?.props only if the widget isn't registered yet.
        const registered = lookupComponent(widget.id);
        const registeredConfigProps = ((_a = registered === null || registered === void 0 ? void 0 : registered.configs) === null || _a === void 0 ? void 0 : _a.props) || ((_b = widget.configs) === null || _b === void 0 ? void 0 : _b.props) || [];
        // Seed each field's value from the current widget props
        const formStructure = registeredConfigProps.map((field) => (Object.assign(Object.assign({}, field), { value: field.name in props ? props[field.name] : field.value })));
        // Get custom config panel if available (registered takes precedence)
        const ConfigPanel = (_e = (((_c = registered === null || registered === void 0 ? void 0 : registered.configs) === null || _c === void 0 ? void 0 : _c.configPanel) || ((_d = widget.configs) === null || _d === void 0 ? void 0 : _d.configPanel))) !== null && _e !== void 0 ? _e : null;
        return { formStructure, currentProps: props, ConfigPanel };
    }, [widget]);
    const handleSave = useCallback((submittedData) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!widget)
            return;
        // Create updated widget instance
        const updatedWidget = Object.assign({}, widget);
        // Update config props values using utility function
        if ((_a = updatedWidget.configs) === null || _a === void 0 ? void 0 : _a.props) {
            updatedWidget.configs.props = updateConfigPropsWithValues(updatedWidget.configs.props, submittedData);
        }
        // Merge existing props with new submitted data
        // This preserves props not in the form while updating configured ones
        updatedWidget.configuredProps = Object.assign(Object.assign({}, (widget.configuredProps || {})), submittedData);
        // Mark as configured
        updatedWidget.hasConfigured = true;
        onSave(updatedWidget);
    }), [widget, onSave]);
    const renderDesignerLink = useCallback(() => {
        if (!widget || !widget.isTemplate || !widget.isDefaultTemplate || !widget.templateKey) {
            return null;
        }
        return (_jsx("div", { className: "uxpcore_dashboard__configdesignerlink", children: _jsx(ButtonComponent, { title: context.$L('uxp-core.text.customize-on-designer'), leftIcon: "fal edit", onClick: () => {
                    // TODO: Implement navigation to designer
                    console.log('Navigate to designer for template:', widget.templateKey);
                }, variant: "secondary" }) }));
    }, [widget, context]);
    const renderConfigContent = useCallback(() => {
        if (!widget)
            return null;
        // Template widget with default template - show designer link
        if (widget.isTemplate && widget.isDefaultTemplate) {
            return renderDesignerLink();
        }
        // Custom config panel provided
        if (ConfigPanel) {
            return (_jsx(ConfigPanel, { configs: currentProps, onSubmit: handleSave, onCancel: onClose, uxpContext: context, instanceId: widget._id }));
        }
        // Standard dynamic form
        if (formStructure.length > 0) {
            return (_jsx(DynamicForm, { formStructure: convertToNewFormStructure(formStructure), onSubmit: handleSave, onCancel: onClose }));
        }
        // No configuration available
        return (_jsx("div", { className: "uxpcore_dashboard__noconfig", children: _jsx("p", { children: context.$L('uxp-core.text.no-configuration-available') }) }));
    }, [widget, ConfigPanel, formStructure, currentProps, handleSave, onClose, context, renderDesignerLink]);
    return (_jsx(Modal, { show: widget !== null, title: context.$L('uxp-core.text.edit-configuration'), onClose: onClose, className: classNames('uxpcore_dashboard__configmodal', { 'uxpcore_dashboard__configmodal--default': !ConfigPanel }), children: renderConfigContent() }));
};
export default DashboardWidgetConfig;
